import { IconButton, Spinner } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { TrainingSessionListView } from "@/api";
import {
  inlineLoadingLineClassName,
  inlineSpinnerClassName,
} from "@/shared/ui/loadingState/public";
import { sessionDisplayName } from "../trainingSessionFormat";
import { sessionDetailedStatus } from "@/modules/training/public";
import { SessionStatusChip } from "@/components/status/SessionStatusChip";
import { SessionCompareChartsSection } from "./SessionCompareChartsSection";
import { SessionCompareConfigSection } from "./SessionCompareConfigSection";
import { sessionSeriesColor } from "./sessionCompareModel";
import type { SessionCompareController } from "./useSessionCompareController";
import styles from "./SessionComparisonViewer.module.css";

type SessionComparisonViewerProps = {
  sessions: readonly TrainingSessionListView[];
  slotById: Readonly<Record<string, number>>;
  controller: SessionCompareController;
  selectedKeys: readonly string[];
  onToggleKey: (key: string) => void;
};

// 비교 창 본문 조립. 범례는 목록 행 스냅샷으로 상세 도착 전에 바로 표시한다. 색은 창을 연
// 시점에 고정한 slotById 를 따른다. 실패하면 섹션을 비우지 않고 배너와 재시도로 알린다.
export function SessionComparisonViewer({
  sessions,
  slotById,
  controller,
  selectedKeys,
  onToggleKey,
}: SessionComparisonViewerProps): ReactNode {
  return (
    <article className={styles.root} aria-label="Training Session 비교">
      <header className={styles.header}>
        <span className={styles.subtitle}>
          {sessions.length}개 Session 비교
          {controller.loading ? (
            <span className={inlineLoadingLineClassName}>
              ·
              <Spinner size="sm" className={inlineSpinnerClassName} />
              불러오는 중…
            </span>
          ) : null}
        </span>
      </header>

      {controller.loadFailed ? (
        <div className={styles.errorNotice} role="alert">
          <span>
            일부 Session 데이터를 불러오지 못했습니다. 값이 없는 항목은 표시되지 않습니다.
          </span>
          <IconButton
            type="button"
            variant="surface"
            size="sm"
            text="다시 시도"
            onClick={controller.retry}
          />
        </div>
      ) : null}

      <div className={styles.legend}>
        {sessions.map((session) => {
          const detail = controller.loads[session.id]?.detail;
          return (
            <SessionLegendChip
              key={session.id}
              session={session}
              // 상세가 오면 폴링한 최신 status 를, 아니면 창을 연 시점의 목록 행 값을 쓴다.
              status={
                detail?.status === "success"
                  ? (detail.data.status ?? session.status)
                  : session.status
              }
              slot={slotById[session.id] ?? 0}
            />
          );
        })}
      </div>

      <SessionCompareChartsSection
        sessions={sessions}
        slotById={slotById}
        controller={controller}
        selectedKeys={selectedKeys}
        onToggleKey={onToggleKey}
      />

      <SessionCompareConfigSection sessions={sessions} controller={controller} />
    </article>
  );
}

// 세션 한 개의 범례 칩. 시리즈 색 스와치, 이름, 상태 칩이다.
function SessionLegendChip({
  session,
  status: statusValue,
  slot,
}: {
  session: TrainingSessionListView;
  status: string;
  slot: number;
}): ReactNode {
  const status = sessionDetailedStatus(statusValue);
  const color = sessionSeriesColor(slot);
  return (
    <span className={styles.chip}>
      <svg width={22} height={8} aria-hidden>
        <line x1={0} y1={4} x2={22} y2={4} stroke={color} strokeWidth={2} />
      </svg>
      <span className={styles.chipLabel}>{sessionDisplayName(session)}</span>
      <SessionStatusChip status={statusValue}>{status.label}</SessionStatusChip>
    </span>
  );
}
